import {
  ComposedChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  Area,
  Line,
  ResponsiveContainer,
} from "recharts";
import { ChartProps } from "./types";
import { useTranslation } from "react-i18next";

const commonTooltipStyle = {
  backgroundColor: "rgba(255, 255, 255, 0.95)",
  border: "1px solid #e5e7eb",
  borderRadius: "8px",
  boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
  color: "#111827",
  padding: "12px",
};

export const PerformanceChart: React.FC<ChartProps> = ({
  data,
  isFullScreen = false,
}) => {
  const { t } = useTranslation();

  const formatMonth = (month: string) => {
    const date = new Date(month);
    return t(
      `common.months.${date.toLocaleDateString("en-US", { month: "long" }).toLowerCase()}`,
    );
  };

  return (
    <div className="col-span-2 bg-white dark:bg-gray-800 rounded-lg p-4">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          {t("analytics.charts.performance")}
        </h3>
      </div>
      <div className={`${!isFullScreen ? "h-96" : "h-[55vh]"}`}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data}>
            <defs>
              <linearGradient id="colorScore" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8} />
                <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.1} />
              </linearGradient>
              <linearGradient id="colorCompletion" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#22c55e" stopOpacity={0.8} />
                <stop offset="95%" stopColor="#22c55e" stopOpacity={0.1} />
              </linearGradient>
              <linearGradient id="colorAssignment" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#9333ea" stopOpacity={0.8} />
                <stop offset="95%" stopColor="#9333ea" stopOpacity={0.1} />
              </linearGradient>
            </defs>
            <CartesianGrid
              strokeDasharray="3 3"
              className="stroke-gray-200 dark:stroke-gray-700"
            />
            <XAxis
              dataKey="month"
              className="text-gray-600 dark:text-gray-300"
              tickFormatter={formatMonth}
            />
            <YAxis
              className="text-gray-600 dark:text-gray-300"
              tickFormatter={(value) => `${value}%`}
            />
            <Tooltip
              contentStyle={commonTooltipStyle}
              formatter={(value: number) => [`${value.toFixed(1)}%`]}
              labelFormatter={(label) => {
                const date = new Date(label);
                const month = t(
                  `common.months.${date.toLocaleDateString("en-US", { month: "long" }).toLowerCase()}`,
                );
                const year = date.getFullYear();
                return `${month} ${year}`;
              }}
            />
            <Legend />
            <Area
              type="monotone"
              dataKey="averageScore"
              stroke="#3b82f6"
              fillOpacity={1}
              fill="url(#colorScore)"
              name={t("analytics.metrics.averageScore")}
            />
            <Area
              type="monotone"
              dataKey="completionRate"
              stroke="#22c55e"
              fillOpacity={1}
              fill="url(#colorCompletion)"
              name={t("analytics.metrics.completionRate")}
            />
            <Area
              type="monotone"
              dataKey="assignmentCompletion"
              stroke="#9333ea"
              fillOpacity={1}
              fill="url(#colorAssignment)"
              name={t("analytics.metrics.assignmentCompletion")}
            />
            <Line
              type="monotone"
              dataKey="participationRate"
              stroke="#f59e0b"
              strokeWidth={2}
              name={t("analytics.metrics.participation")}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default PerformanceChart;
